import { useEffect, useRef } from 'react';

import { savePushTokenMutation } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { getPushRegistration, loadFirebaseMessaging, VAPID_KEY } from '@helpers/push/firebaseMessaging';
import { usePushPermission } from '@helpers/push/pushPermission';
import { savePushTokenOnce } from '@helpers/push/pushToken';

export interface ForegroundPush {
  title: string;
  body?: string;
  link?: string;
}

/*
 * Registers this browser for Chrome notifications while a staff member is signed in and Chrome
 * has granted permission. Mounted by the bell, which is in the top bar of every signed-in page.
 *
 * It reads the shared permission store, so granting from the banner or the alerts page starts it
 * at once, without a reload. Releasing the token at sign-out is not here: it is signOutPush, run
 * by the logout saga, so it happens on every sign-out path whether or not a bell is mounted.
 *
 * Which alerts arrive is not decided here either. The notification service sends a push for
 * exactly the admin_notifications the bell would show this person (their alert preferences
 * decide), and skips it when their Chrome notifications switch is off.
 */
export function usePushNotifications({
  userId,
  onForegroundMessage,
}: {
  userId?: string;
  onForegroundMessage?: (push: ForegroundPush) => void;
}) {
  const permission = usePushPermission();

  // A ref, so a new callback identity does not tear down and redo the whole registration.
  const onForegroundRef = useRef(onForegroundMessage);
  useEffect(() => {
    onForegroundRef.current = onForegroundMessage;
  }, [onForegroundMessage]);

  useEffect(() => {
    if (!userId || permission !== 'granted') return;

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      try {
        const registration = await getPushRegistration({ register: true });
        if (!registration || cancelled) return;

        const { messaging, getToken, onMessage } = await loadFirebaseMessaging();
        if (!messaging || cancelled) return;

        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (!token || cancelled) return;

        let storage: Storage | null = null;
        try { storage = window.localStorage; } catch { storage = null; }
        await savePushTokenOnce({
          token,
          userId,
          storage,
          save: (t) => fetchGraphQLApiData(savePushTokenMutation(t, navigator.userAgent.slice(0, 120))),
        });
        if (cancelled) return;

        // A focused console gets no system notification from FCM; it gets this callback instead,
        // and the bell turns it into a fresh count and a toast.
        unsubscribe = onMessage(messaging, (payload) => {
          onForegroundRef.current?.({
            title: payload.notification?.title ?? payload.data?.title ?? 'New alert',
            body: payload.notification?.body ?? payload.data?.body,
            link: payload.data?.link ?? payload.data?.url ?? payload.fcmOptions?.link,
          });
        });
      } catch (err) {
        console.warn('[push] registration failed', err);
      }
    })();

    return () => {
      cancelled = true;
      // Otherwise a signed-out page, or the next person's, keeps receiving the last one's alerts.
      unsubscribe?.();
    };
  }, [userId, permission]);
}
